import { ACTIVATION_LABELS, ec2InstancesLabel } from '../classification';
import type {
  ClusterTop,
  DrillLocation,
  ExplorationVolume,
  StructuralLocation,
} from '../../types/exploration';

export const EC2_INSTANCES_KEY = ec2InstancesLabel;
export const UNATTACHED_KEY = 'Unattached';

export function emptyDrillLocation(): DrillLocation {
  return { structural: { kind: 'empty' } };
}

export function clusterTopKeyString(top: ClusterTop): string {
  switch (top.kind) {
    case 'cluster':
      return top.name;
    case 'ec2':
      return EC2_INSTANCES_KEY;
    case 'unattached':
      return UNATTACHED_KEY;
  }
}

/** The structural top a volume belongs to — known from its own fields, never guessed. */
export function clusterTopForVolume(v: ExplorationVolume): ClusterTop {
  if (v.cluster) return { kind: 'cluster', name: v.cluster };
  if (v.instanceId) return { kind: 'ec2' };
  return { kind: 'unattached' };
}

export function isDrillLocationEmpty(location: DrillLocation): boolean {
  return (
    location.structural.kind === 'empty' &&
    !location.account &&
    !location.statusLabel &&
    !location.focusedActivationKey &&
    !location.reasonLabel
  );
}

function clusterTopEqual(a: ClusterTop, b: ClusterTop): boolean {
  if (a.kind !== b.kind) return false;
  return a.kind === 'cluster' && b.kind === 'cluster' ? a.name === b.name : true;
}

function structuralEqual(a: StructuralLocation, b: StructuralLocation): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'cluster' && b.kind === 'cluster') {
    return clusterTopEqual(a.top, b.top) && a.nodeGroup === b.nodeGroup && a.instanceId === b.instanceId;
  }
  if (a.kind === 'region' && b.kind === 'region') {
    return a.region === b.region && a.instanceId === b.instanceId;
  }
  if (a.kind === 'tag' && b.kind === 'tag') {
    return a.tagValue === b.tagValue && a.instanceId === b.instanceId;
  }
  return true;
}

export function drillLocationsEqual(a: DrillLocation, b: DrillLocation): boolean {
  return (
    a.account === b.account &&
    a.statusLabel === b.statusLabel &&
    a.focusedActivationKey === b.focusedActivationKey &&
    a.reasonLabel === b.reasonLabel &&
    structuralEqual(a.structural, b.structural)
  );
}

function popStructural(structural: StructuralLocation): StructuralLocation | null {
  switch (structural.kind) {
    case 'cluster':
      if (structural.instanceId) return { ...structural, instanceId: undefined };
      if (structural.nodeGroup) return { ...structural, nodeGroup: undefined };
      return { kind: 'empty' };
    case 'region':
      if (structural.instanceId) return { ...structural, instanceId: undefined };
      return { kind: 'empty' };
    case 'tag':
      if (structural.instanceId) return { ...structural, instanceId: undefined };
      return { kind: 'empty' };
    case 'empty':
      return null;
  }
}

/**
 * One drill-up step: pop the innermost populated facet, in the order a user
 * navigated them in (reason tail → status/focus tail → innermost structural
 * component → account). No-op (returns the same location) once empty —
 * callers should check `isDrillLocationEmpty` first.
 */
export function popDrillLocation(location: DrillLocation): DrillLocation {
  if (location.reasonLabel) return { ...location, reasonLabel: undefined };
  if (location.focusedActivationKey) return { ...location, focusedActivationKey: undefined };
  if (location.statusLabel) return { ...location, statusLabel: undefined };

  const structural = popStructural(location.structural);
  if (structural) return { ...location, structural };

  if (location.account) return { ...location, account: undefined };

  return location;
}

/**
 * True when `ancestor` equals `location` or can be reached by repeatedly
 * popping `location` (parent-ring / equality check for canvas clicks).
 * Callers that want root self-hit as noop should check equality first.
 */
export function isDrillLocationAncestor(
  ancestor: DrillLocation,
  location: DrillLocation
): boolean {
  if (drillLocationsEqual(ancestor, location)) return true;
  let current = location;
  for (let i = 0; i < 16; i++) {
    if (isDrillLocationEmpty(current)) return false;
    const parent = popDrillLocation(current);
    if (drillLocationsEqual(parent, current)) return false;
    if (drillLocationsEqual(parent, ancestor)) return true;
    current = parent;
  }
  return false;
}

export interface StructuralPathOptions {
  groupByAccount: boolean;
}

/**
 * Positional projection of the structural facet to an ordered string list —
 * the shape the hierarchy tree-builder and tooltip ancestry already expect
 * internally (account?, top, nodeGroup?/region?/tagValue?, instanceId?).
 * Never used for interpretation, only for display/positional consumers that
 * already know their own schema.
 */
export function structuralPathStrings(
  location: Pick<DrillLocation, 'account' | 'structural'>,
  opts: StructuralPathOptions
): string[] {
  const path: string[] = [];
  if (opts.groupByAccount && location.account) path.push(location.account);

  const s = location.structural;
  if (s.kind === 'cluster') {
    path.push(clusterTopKeyString(s.top));
    if (s.nodeGroup) path.push(s.nodeGroup);
    if (s.instanceId) path.push(s.instanceId);
  } else if (s.kind === 'region') {
    if (s.region) path.push(s.region);
    if (s.instanceId) path.push(s.instanceId);
  } else if (s.kind === 'tag') {
    if (s.tagValue) path.push(s.tagValue);
    if (s.instanceId) path.push(s.instanceId);
  }
  return path;
}

/** Full positional projection (structural + status/focus/reason overlay), for breadcrumb display. */
export function displayPathStrings(
  location: DrillLocation,
  opts: StructuralPathOptions
): string[] {
  const path = structuralPathStrings(location, opts);
  if (location.focusedActivationKey) {
    path.push(ACTIVATION_LABELS[location.focusedActivationKey]);
  } else if (location.statusLabel) {
    path.push(location.statusLabel);
  }
  if (location.reasonLabel) path.push(location.reasonLabel);
  return path;
}

type LocationStep = (loc: DrillLocation) => DrillLocation;

/** Location truncated to the breadcrumb item at `index` (see `displayPathStrings`'s ordering). */
export function locationAtDisplayIndex(
  location: DrillLocation,
  index: number,
  opts: StructuralPathOptions
): DrillLocation {
  const steps: LocationStep[] = [];

  if (opts.groupByAccount && location.account) {
    const account = location.account;
    steps.push((loc) => ({ ...loc, account }));
  }

  const s = location.structural;
  if (s.kind === 'cluster') {
    const top = s.top;
    steps.push((loc) => ({ ...loc, structural: { kind: 'cluster', top } }));
    if (s.nodeGroup) {
      const nodeGroup = s.nodeGroup;
      steps.push((loc) =>
        loc.structural.kind === 'cluster' ? { ...loc, structural: { ...loc.structural, nodeGroup } } : loc
      );
    }
    if (s.instanceId) {
      const instanceId = s.instanceId;
      steps.push((loc) =>
        loc.structural.kind === 'cluster' ? { ...loc, structural: { ...loc.structural, instanceId } } : loc
      );
    }
  } else if (s.kind === 'region') {
    if (s.region) {
      const region = s.region;
      steps.push((loc) => ({ ...loc, structural: { kind: 'region', region } }));
    }
    if (s.instanceId) {
      const instanceId = s.instanceId;
      steps.push((loc) =>
        loc.structural.kind === 'region' ? { ...loc, structural: { ...loc.structural, instanceId } } : loc
      );
    }
  } else if (s.kind === 'tag') {
    if (s.tagValue) {
      const tagValue = s.tagValue;
      steps.push((loc) => ({ ...loc, structural: { kind: 'tag', tagValue } }));
    }
    if (s.instanceId) {
      const instanceId = s.instanceId;
      steps.push((loc) =>
        loc.structural.kind === 'tag' ? { ...loc, structural: { ...loc.structural, instanceId } } : loc
      );
    }
  }

  if (location.focusedActivationKey) {
    const focusedActivationKey = location.focusedActivationKey;
    steps.push((loc) => ({ ...loc, focusedActivationKey }));
  } else if (location.statusLabel) {
    const statusLabel = location.statusLabel;
    steps.push((loc) => ({ ...loc, statusLabel }));
  }

  if (location.reasonLabel) {
    const reasonLabel = location.reasonLabel;
    steps.push((loc) => ({ ...loc, reasonLabel }));
  }

  let result = emptyDrillLocation();
  for (let i = 0; i <= index && i < steps.length; i++) {
    result = steps[i]!(result);
  }
  return result;
}

/** True once the structural facet has reached a specific instance leaf. */
export function isInstanceLocation(location: DrillLocation): boolean {
  const s = location.structural;
  return (s.kind === 'cluster' || s.kind === 'region' || s.kind === 'tag') && !!s.instanceId;
}

/** True when there's a structural drill in effect with no status/reason overlay on top. */
export function isPureStructuralDrill(location: DrillLocation): boolean {
  return (
    location.structural.kind !== 'empty' &&
    !location.statusLabel &&
    !location.focusedActivationKey &&
    !location.reasonLabel
  );
}
